/**
 * Date/place formatting. Events carry their own IANA `timezone`, so a London event reads as
 * 7pm to everyone — formatting in the viewer's local zone would quietly shift the time for
 * anyone travelling or on a mis-set machine.
 */

const DEFAULT_TIME_ZONE = 'Europe/London';

export function formatDateTime(iso: string, timeZone: string = DEFAULT_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(new Date(iso));
}

export function formatDate(iso: string, timeZone: string = DEFAULT_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone,
  }).format(new Date(iso));
}

/**
 * "Sat 6 Sep", or "Sat 6 Sep 2027" once the year stops being obvious.
 *
 * <p>For the timetable's date block, which is a small centred tile — {@link formatDate}'s
 * "6 September 2026" is three times its width and forces the column wide enough to unbalance the
 * whole row. The weekday earns its place here in a way the year doesn't: people choosing an event
 * care what day of the week it falls on far more than they care that it's still this year.
 */
export function formatDateCompact(iso: string, timeZone: string = DEFAULT_TIME_ZONE): string {
  const date = new Date(iso);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone,
  }).format(date);
}

export function formatTime(iso: string, timeZone: string = DEFAULT_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(new Date(iso));
}

/** "Starts in 3 days" / "Starts tomorrow". Whole days, from the start of today. */
export function formatRelativeDays(iso: string, now: Date = new Date()): string {
  const msPerDay = 86_400_000;
  const startOfToday = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(iso);
  const startOfTarget = Date.UTC(target.getFullYear(), target.getMonth(), target.getDate());
  const days = Math.round((startOfTarget - startOfToday) / msPerDay);

  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days < 0) return new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' }).format(days, 'day');
  if (days < 7) return `In ${days} days`;
  if (days < 14) return 'Next week';
  return `In ${Math.round(days / 7)} weeks`;
}

/** Venue, address, city, postcode — whichever are present, comma-joined. */
export function formatLocation(parts: Array<string | null | undefined>): string {
  return parts.filter((p): p is string => !!p && p.trim().length > 0).join(', ');
}
